import React, { useLayoutEffect, useRef, useState } from "react";
import { Modal, Skeleton } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import XIconSvg from "../../../../common/svgs/XIconSvg";
import PrimaryButton from "../../../../common/ui/PrimaryButton";
import StartServiceElement from "./StartServiceElement";
import LabPanelQuestionFields from "../common/LabPanelQuestionFields";
import { displayLabPanelName } from "../../utils/labPanelName";
import {
  getPanelQuestions,
  isQuestionUnanswered,
  LabPanelQuestion,
} from "../../utils/labPanelQuestions";

interface EditLabPanelModalProps {
  open: boolean;
  labPanel: any | null;
  onClose: () => void;
  onSave: (updatedLabPanel: any) => Promise<void>;
  onDelete?: (labPanel: any) => void;
  canDelete?: boolean;
  type?: "logicBuilder" | "prescriptionBuilder";
  // The panel is still being fetched: the modal opens at once on a skeleton
  // shaped like the content it is about to show. `loadingQuestionCount`
  // sizes the skeleton's question list so nothing jumps when the panel
  // arrives (prescription mode only).
  loading?: boolean;
  loadingQuestionCount?: number;
}

const EditLabPanelModal = ({
  open,
  labPanel,
  onClose,
  onSave,
  onDelete,
  canDelete = true,
  type = "logicBuilder",
  loading = false,
  loadingQuestionCount = 0,
}: EditLabPanelModalProps) => {
  const theme: Theme = useTheme();
  const [editedPanel, setEditedPanel] = useState<any>(labPanel);
  const [isSaving, setIsSaving] = useState(false);
  // While saving, the content is swapped for a skeleton pinned to the height
  // the content had, so the modal keeps its size until it closes.
  const contentRef = useRef<HTMLDivElement>(null);
  const [savingHeight, setSavingHeight] = useState<number | null>(null);

  React.useEffect(() => {
    if (labPanel) {
      setEditedPanel(labPanel);
    }
  }, [labPanel]);

  useLayoutEffect(() => {
    if (!open) setSavingHeight(null);
  }, [open]);

  const handleServiceChange = (updatedService: any) => {
    if (type === "prescriptionBuilder") {
      setEditedPanel({
        ...editedPanel,
        startDate: updatedService.startDate,
      });
    } else {
      setEditedPanel({
        ...editedPanel,
        startOffset: updatedService.startOffset,
        startOffsetUnit: updatedService.startOffsetUnit,
      });
    }
  };

  // The lab's order-entry questions, answered here when a prescribed panel is
  // edited — the same fields the prescription builder shows. The answers live
  // on each question's `userAnswer` and go back with the panel on save. The
  // logic builder edits a template, so it shows none.
  const isPrescription = type === "prescriptionBuilder";
  const questions: LabPanelQuestion[] =
    isPrescription && editedPanel ? getPanelQuestions(editedPanel) : [];
  const unansweredCount = questions.filter(isQuestionUnanswered).length;

  const handleQuestionsChange = (updated: LabPanelQuestion[]) => {
    setEditedPanel({ ...editedPanel, questions: updated });
  };

  const handleSave = async () => {
    if (!editedPanel || unansweredCount > 0 || isSaving) return;

    setSavingHeight(contentRef.current?.offsetHeight ?? null);
    setIsSaving(true);
    try {
      await onSave(editedPanel);
      onClose();
    } catch (error) {
      console.error("Error saving lab panel:", error);
    } finally {
      setIsSaving(false);
      setSavingHeight(null);
    }
  };

  const handleDelete = () => {
    if (onDelete && editedPanel) {
      onDelete(editedPanel);
    }
  };

  if (!labPanel && !loading) return null;

  const syntheticService =
    type === "prescriptionBuilder"
      ? {
          startDate: editedPanel?.startDate ?? null,
        }
      : {
          startOffset: editedPanel?.startOffset ?? 0,
          startOffsetUnit: editedPanel?.startOffsetUnit ?? 4,
        };

  const showSkeleton = isSaving || loading || !editedPanel;
  const busy = isSaving || loading;

  return (
    <Modal open={open} onClose={onClose} disableEnforceFocus>
      <div style={styles.modalContainer(theme)}>
        {/* Modal Header */}
        <div style={styles.modalHeaderContainer}>
          <div style={styles.modalHeaderTitle(theme)}>Edit lab panel</div>
          <div onClick={onClose} style={styles.iconWrapper}>
            <XIconSvg />
          </div>
        </div>

        {/* Modal Content */}
        {showSkeleton ? (
          <EditLabPanelSkeleton
            height={savingHeight}
            questionCount={
              isSaving ? questions.length : isPrescription ? loadingQuestionCount : 0
            }
          />
        ) : (
          <div ref={contentRef} style={styles.contentContainer(theme)}>
            {/* Panel name */}
            <div style={styles.panelHeader}>
              {/* A logic's snapshot carries the internal "<name>.<guid>";
                  show the plain name, as the logic detail does. */}
              <div style={styles.panelTitle(theme)}>
                {displayLabPanelName(editedPanel.name) || "Lab Panel"}
              </div>
              <div style={styles.panelType(theme)}>Lab panel</div>
            </div>

            {/* Start offset / Start date */}
            <div style={styles.startOffsetGrid}>
              <StartServiceElement
                service={syntheticService}
                onServiceChange={handleServiceChange}
                type={type}
              />
            </div>

            {/* Order-entry questions — prescribed panels only */}
            <LabPanelQuestionFields
              questions={questions}
              onChange={handleQuestionsChange}
              panel={editedPanel}
              singleColumn
            />
          </div>
        )}

        {/* Footer with Save and Delete buttons. Stays put while the content
            is skeletoned so the modal keeps its size; both wait for the
            busy state to end. */}
        <div style={styles.footer}>
          {onDelete && canDelete && (
            <div style={{ flex: 1 }}>
              <PrimaryButton
                label={"Delete"}
                onClick={handleDelete}
                isDisable={busy}
                sx={{
                  backgroundColor: theme.colorTokens.D[50],
                  "&:hover": {
                    backgroundColor: theme.colorTokens.D[60],
                  },
                }}
              />
            </div>
          )}
          {/* Every question the lab asks is required, so Save waits for
              the last answer — the order cannot be placed without them. */}
          <PrimaryButton
            label={
              isSaving
                ? "Saving…"
                : !busy && unansweredCount > 0
                ? `Answer ${unansweredCount} more question${
                    unansweredCount === 1 ? "" : "s"
                  }`
                : "Save"
            }
            onClick={handleSave}
            isDisable={busy || unansweredCount > 0}
          />
        </div>
      </div>
    </Modal>
  );
};

export default EditLabPanelModal;

/**
 * The content container's shape in grey: panel name and type, the start
 * field, and (prescription mode) the questions title, note and one input
 * per question — same paddings, gaps and field heights as the real thing, so
 * the modal does not change size when the content appears or is saved. With
 * `height` (the measured content, while saving) the box is pinned to it.
 */
const EditLabPanelSkeleton = ({
  height,
  questionCount,
}: {
  height: number | null;
  questionCount: number;
}) => {
  const theme: Theme = useTheme();
  const block = (width: string, h: string) => (
    <Skeleton
      variant="rectangular"
      sx={{
        width,
        height: h,
        borderRadius: "4px",
        backgroundColor: theme.colorTokens.N[30],
      }}
    />
  );
  const field = (key: React.Key) => (
    <Skeleton
      key={key}
      variant="rectangular"
      sx={{
        width: "100%",
        height: "56px",
        borderRadius: "8px",
        backgroundColor: theme.colorTokens.N[30],
      }}
    />
  );

  return (
    <div
      aria-busy="true"
      style={{
        ...styles.contentContainer(theme),
        ...(height ? { height: `${height}px`, boxSizing: "border-box" } : {}),
        overflow: "hidden",
      }}
    >
      <div style={styles.panelHeader}>
        {block("40%", "20px")}
        {block("64px", "17px")}
      </div>
      <div style={styles.startOffsetGrid}>
        <div style={styles.skeletonFieldWithLabel}>
          {block("72px", "17px")}
          {field("start")}
        </div>
      </div>
      {questionCount > 0 && (
        <div>
          <div style={{ paddingBottom: "8px" }}>{block("96px", "17px")}</div>
          <div style={{ paddingBottom: "12px" }}>{block("70%", "14px")}</div>
          <div style={styles.startOffsetGrid}>
            {Array.from({ length: questionCount }).map((_, index) =>
              field(index)
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  modalContainer: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    padding: "24px",
    width: "80%",
    minWidth: "400px",
    maxWidth: "600px",
    maxHeight: "90vh",
    borderRadius: "8px",
    boxShadow: "var(--box-shadow-general)",
    gap: "32px",
    outline: "none",
  }),
  modalHeaderContainer: {
    display: "flex",
    gap: "16px",
    alignItems: "flex-start",
  },
  modalHeaderTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "24px",
    lineHeight: "34px",
    flex: 1,
  }),
  iconWrapper: {
    marginTop: "10px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  contentContainer: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
    padding: "16px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    // The questions list can outgrow the viewport: the content scrolls
    // inside the modal rather than the modal growing past the screen.
    overflowY: "auto" as "auto",
    minHeight: 0,
  }),
  panelHeader: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
  },
  panelTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  panelType: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  startOffsetGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "16px",
  },
  skeletonFieldWithLabel: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "16px",
  },
};
